// DreamTeamPin — the pinned, staff-only "Dream Team" row anchored to the bottom of the sidebar.
// Uses the animated DreamTeamLogo emblem. Right-click / long-press opens the same context menu the
// product-channel rows have (Summarize unread + Mute), so staff get parity here too.
import { useRef, useState, useEffect } from "react";
import DreamTeamLogo from "./DreamTeamLogo.jsx";
import { useTheme } from "../hooks/useTheme.js";
import Icon from "./Icon.jsx";

// A glowing "scrying orb" unread badge — orb holding the count, internal mist swirl, pulsing glow, a
// speech-bubble, and a one-shot pop + burst ring each time the count climbs (new message). Toggling
// the a/b animation-name re-triggers the one-shots without a JS reset. Shared by the spooky (coven,
// dtu-) and deep-space (crew, dcu-) pins — only the class base, anim prefix, and bubble hue differ.
function OrbUnread({ count, base, prefix, bubbleStroke }) {
  const [bump, setBump] = useState(0);
  const prev = useRef(count);
  useEffect(() => {
    if (count > prev.current) setBump((b) => b + 1);
    prev.current = count;
  }, [count]);
  const odd = bump % 2 === 1;
  const countAnim = `${prefix}-countpulse 2.6s ease-in-out infinite, ${odd ? `${prefix}-pop-a` : `${prefix}-pop-b`} .6s ease`;
  const burstAnim = bump > 0 ? `${odd ? `${prefix}-burst-a` : `${prefix}-burst-b`} .7s ease-out` : "none";
  const label = count > 99 ? "99+" : String(count);
  return (
    <span className={base} aria-label={`${count} unread`}>
      <span className={`${base}__orb`} aria-hidden>
        <span className={`${base}__mist`} />
        <span className={`${base}__burst`} style={{ animation: burstAnim }} />
        <span className={`${base}__count ${label.length > 2 ? "is-long" : ""}`} style={{ animation: countAnim }}>{label}</span>
      </span>
      <svg className={`${base}__bubble`} viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <path d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4v-4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" fill="#eaf3ff" stroke={bubbleStroke} strokeWidth="1.4" />
        <circle cx="8" cy="10.5" r="1.3" fill={bubbleStroke} /><circle cx="12" cy="10.5" r="1.3" fill={bubbleStroke} /><circle cx="16" cy="10.5" r="1.3" fill={bubbleStroke} />
      </svg>
    </span>
  );
}

// Five spirit-ghosts (white / violet / amber / green / blue) trailing around the pin's rounded
// border via offset-path. Lives on a NON-clipped overlay so they can sit half over the edge.
const GHOSTS = [
  { fill: "#ffffff", glow: "rgba(255,255,255,.8)", delay: "0s" },
  { fill: "#c7a4ff", glow: "rgba(160,107,255,.85)", delay: "-0.5s" },
  { fill: "#ffbe7a", glow: "rgba(255,190,122,.85)", delay: "-1s" },
  { fill: "#9ff29a", glow: "rgba(107,217,104,.85)", delay: "-1.5s" },
  { fill: "#8fd0ff", glow: "rgba(95,168,255,.85)", delay: "-2s" },
];
function GhostTrain() {
  return (
    <div className="dt-pin__ghosts" aria-hidden>
      {GHOSTS.map((g, i) => (
        <span key={i} className="dt-pin__ghost" style={{ animationDelay: g.delay, filter: `drop-shadow(0 0 3px ${g.glow})` }}>
          <svg viewBox="0 0 12 12" width="15" height="15">
            <path d="M6 .8C3.4.8 1.7 2.6 1.7 5v5.4c0 .5.5.7.9.4l.9-.7.9.7c.2.2.5.2.7 0l.9-.7.9.7.9-.7c.2-.2.5-.2.7 0l.9.7c.4.3.9.1.9-.4V5C10.3 2.6 8.6.8 6 .8z" fill={g.fill} />
            <circle cx="4.6" cy="4.8" r=".9" fill="#2a1747" /><circle cx="7.4" cy="4.8" r=".9" fill="#2a1747" />
          </svg>
        </span>
      ))}
    </div>
  );
}
// Stormy backdrop (clouds + rain + lightning) behind the pin card; sits inside the clipped row.
function StormBackdrop() {
  return (
    <span className="dt-pin__storm" aria-hidden>
      <span className="dt-storm__cloud dt-storm__cloud--1" />
      <span className="dt-storm__cloud dt-storm__cloud--2" />
      <span className="dt-storm__rain" />
      <span className="dt-storm__flash dt-storm__flash--1" />
      <span className="dt-storm__flash dt-storm__flash--2" />
      <svg className="dt-storm__bolt" viewBox="0 0 40 60" width="22" height="33"><path d="M22 0 L10 26 H19 L8 60 L34 22 H23 L30 0 Z" fill="#eaf0ff" /></svg>
    </span>
  );
}

// Deep-space night-sky accents behind the pin card (right-biased) — a teal glow, twinkling stars,
// and one shooting star. Sits inside the clipped row.
function SkyAccents() {
  return (
    <span className="dt-pin__sky" aria-hidden>
      <span className="dt-pin__sky-glow" />
      <span className="dt-pin__star" style={{ left: "60%", top: "22%" }} />
      <span className="dt-pin__star" style={{ left: "74%", top: "56%", animationDelay: ".6s", animationDuration: "3.4s" }} />
      <span className="dt-pin__star" style={{ left: "88%", top: "30%", width: "1.6px", height: "1.6px", animationDelay: "1s", animationDuration: "2.8s" }} />
      <span className="dt-pin__star" style={{ left: "66%", top: "70%", width: "1.6px", height: "1.6px", background: "#9fd8ff", animationDelay: ".3s", animationDuration: "3.6s" }} />
      <span className="dt-pin__shootstar" />
    </span>
  );
}
// Three little satellites riding the pin's rounded border via offset-path (deep-space counterpart
// of the coven's ghost train). Non-clipped overlay so they sit half over the edge.
const SATS = [
  { color: "#7fe9df", delay: "0s" },
  { color: "#ffb454", delay: "-5s" },
  { color: "#9fd8ff", delay: "-10s" },
];
function SatelliteTrain() {
  return (
    <div className="dt-pin__sats" aria-hidden>
      {SATS.map((s, i) => (
        <span key={i} className="dt-pin__sat" style={{ animationDelay: s.delay, filter: "drop-shadow(0 0 3px rgba(120,220,255,.8))" }}>
          <svg viewBox="0 0 16 16" width="14" height="14">
            <rect x="2" y="6" width="4" height="4" fill={s.color} /><rect x="10" y="6" width="4" height="4" fill={s.color} />
            <rect x="6" y="5" width="4" height="6" rx="1" fill="#eaf3ff" /><line x1="8" y1="2" x2="8" y2="5" stroke={s.color} strokeWidth="1" />
          </svg>
        </span>
      ))}
    </div>
  );
}

export default function DreamTeamPin({ channel, activeId, onSelect, isStaff = false, onSummarizeUnread, onMute }) {
  const { theme } = useTheme();
  const spooky = theme === "spooky";
  const deep = theme === "deep";
  if (!channel) return null;
  const active = channel.id === activeId;
  const unread = channel.unreadCount ?? channel.unread ?? 0;
  const muted = channel.isMuted ?? channel.muted ?? false;

  // Right-click (desktop) / long-press (touch) → context menu, mirroring ChannelList.
  const [menu, setMenu] = useState(null); // { x, y } | null
  const pressTimer = useRef(null);
  const suppressClick = useRef(false);
  const openMenu = (x, y) => setMenu({ x: Math.min(x, window.innerWidth - 190), y });
  const closeMenu = () => setMenu(null);
  const startPress = (e) => {
    if (e.pointerType === "mouse") return; // desktop uses onContextMenu
    suppressClick.current = false;
    pressTimer.current = setTimeout(() => { suppressClick.current = true; openMenu(e.clientX, e.clientY); }, 500);
  };
  const cancelPress = () => { if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; } };
  const click = () => { if (suppressClick.current) { suppressClick.current = false; return; } onSelect(channel); };

  return (
    <div className="dt-pin-wrap" data-tour="dreamteam">
      <div className="dt-pin-label"><span className="dt-pin-dot" />{spooky ? "The coven · Pinned" : deep ? "Dream Team · Pinned" : "Internal · Pinned"}</div>
      <div className={`dt-pin-shell ${spooky ? "dt-pin-shell--spooky" : deep ? "dt-pin-shell--deep" : ""}`}>
        <button className={`dt-pin ${active ? "dt-pin--active" : ""}`} onClick={click}
                onContextMenu={(e) => { e.preventDefault(); openMenu(e.clientX, e.clientY); }}
                onPointerDown={startPress} onPointerUp={cancelPress} onPointerLeave={cancelPress} onPointerCancel={cancelPress}>
          {spooky && <StormBackdrop />}
          {deep && <SkyAccents />}
          <span className="dt-pin__sheen" aria-hidden />
          <DreamTeamLogo size={46} />
          <span className="dt-pin__meta">
            <span className="dt-pin__name">Dream Team</span>
            <span className="dt-pin__sub">Staff ops · all products</span>
          </span>
          {muted && <span className="channel__mute">🔕</span>}
          {unread > 0 && !active && (spooky
            ? <OrbUnread count={unread} base="dt-coven-unread" prefix="dtu" bubbleStroke="#5fa8ff" />
            : deep
              ? <OrbUnread count={unread} base="dt-crew-unread" prefix="dcu" bubbleStroke="#35e0d0" />
              : <span className="unread dt-pin__unread">{unread}</span>)}
        </button>
        {spooky && <GhostTrain />}
        {deep && <SatelliteTrain />}
      </div>

      {menu && (
        <>
          <div className="chan-menu__overlay" onClick={closeMenu} onContextMenu={(e) => { e.preventDefault(); closeMenu(); }} />
          <div className="chan-menu" style={{ left: menu.x, top: menu.y }}>
            {isStaff && unread > 10 && (
              <button className="chan-menu__item" onClick={() => { onSummarizeUnread?.(channel); closeMenu(); }}>
                <Icon name="refresh" className="ic--sm" />
                Summarize {unread} unread
              </button>
            )}
            <button className="chan-menu__item" onClick={() => { onMute?.(channel, !muted); closeMenu(); }}>
              <Icon name={muted ? "volume" : "volume-x"} className="ic--sm" />
              {muted ? "Unmute channel" : "Mute channel"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
